import { useEffect, useState } from "react";
import { Button, Form, Select, Table } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import tableUtils from "../../utils/tableUtils";
import { CompaniesColumn } from "../../shared/table/columns";
import {
    Actions,
} from "../../shared/constants";
import validateMessages from "../../utils/validate";
import HrnModal from "../../components/HrnModal";
import Icon from "../../components/Icon";
import globalUtils from "../../utils/globalUtils";
import { useCreateCompanyMutation, useDeleteCompanyMutation, useLazyGetCompaniesQuery, useUpdateCompanyMutation } from "../../services/company";
import CompanyForm from "./CompanyForm";
import { useGetBusinessTypesQuery } from "../../services/misc";

const Company = () => {
    const [companyForm] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("");
    const [editId, setEditId] = useState("");
    const [dataSource, setDataSource] = useState<any[]>([]);
    const orphanageId = "00000000-0000-0000-0000-000000000000";
    const orphanCompany: any = { id: orphanageId, name: "Orphanage", isActive: true };
    const [selectedBusinessTypeId, setSelectedBusinessTypeId] = useState<number | null>(null);
    const {
        data: businessTypes,
        isFetching: isBTFetching,
        isSuccess: isBTSuccess,
    } = useGetBusinessTypesQuery({});

    const [getCompanies, getCompaniesResult] = useLazyGetCompaniesQuery();

    useEffect(() => {
        getCompanies({}).unwrap();
    }, []);

    const onBusinessTypeChanged = (value: number) => {
        setSelectedBusinessTypeId(value);
        getCompanies({ businessTypeId: value }).unwrap();
    };
    const [createCompany, createCompanyResult] = useCreateCompanyMutation();
    const [updateCompany, updateCompanyResult] = useUpdateCompanyMutation();
    const [deleteCompany, deleteCompanyResult] = useDeleteCompanyMutation();

    useEffect(() => {
        if (getCompaniesResult.isSuccess) {
            setDataSource([orphanCompany, ...getCompaniesResult.data]);
        }
    }, [getCompaniesResult.isFetching]);

    const showModal = () => {
        setMode(Actions.CREATE);
        setOpen(true);
    };

    const hideModal = () => {
        setOpen(false);
        companyForm.resetFields();
        setMode("");
    };

    const onSave = async () => {
        try {
            await companyForm.validateFields();
        } catch (errorInfo) {
            return;
        }

        const formFields = companyForm.getFieldsValue();

        if (mode === Actions.CREATE) {
            // ** try catch must need for mutation!
            try {
                await createCompany(formFields).unwrap();
                setOpen(false);
                companyForm.resetFields();
            } catch (e) { }
        } else {
            try {
                await updateCompany({ id: editId, body: formFields }).unwrap();
                setOpen(false);
                companyForm.resetFields();
            } catch (e) { }
        }
    };

    const handleEdit = (record: any) => {
        if (record.id === orphanageId) return;
        setMode(Actions.EDIT);
        setOpen(true);
        if (record.id) setEditId(record.id);
        companyForm.setFieldsValue(record);
    };

    const handleDelete = async (record: any) => {
        if (record.id === orphanageId) return;
        try {
            await deleteCompany({ id: record.id }).unwrap();
        } catch (e) { }
    };

    return (
        <>
            <div className="flex justify-between items-center gap-3 pb-6">
                <div className="text-headline">{"Companies"}</div>
                <Button type="primary" size="large" className="flex-end" onClick={showModal}>
                    <div className="flex items-center gap-1.5">
                        <Icon icon="add" className="h-4.5 w-4.5" />
                        <div>{"Add Company"}</div>
                    </div>
                </Button>
            </div>
            <div className="flex justify-between flex-wrap items-center gap-4 pb-4">
                <div className="flex items-center flex-wrap gap-3">
                    <Select
                        className="w-[200px]"
                        placeholder={"Business type"}
                        allowClear
                        defaultValue={selectedBusinessTypeId}
                        onChange={onBusinessTypeChanged}
                        loading={isBTFetching}
                        options={
                            isBTSuccess &&
                            businessTypes?.map((x: any) => ({
                                label: x.name,
                                value: x.id,
                            }))
                        }
                    />
                </div>
                <div className="flex items-center gap-x-4">
                    <div className="font-medium">
                        {dataSource?.length}{" "}
                        {globalUtils.checkIsSingular(dataSource.length)
                            ? "Company"
                            : "Companies"}
                    </div>
                </div>
            </div>
            <div>
                <HrnModal
                    open={open}
                    title={`${mode === Actions.CREATE ? "Add" : "Edit"} Company`}
                    width={520}
                    isLoading={
                        (createCompanyResult.isLoading ||
                            updateCompanyResult.isLoading) as boolean
                    }
                    onCancel={hideModal}
                    onSave={onSave}
                >
                    <div className="mt-6">
                        <Form
                            form={companyForm}
                            layout="vertical"
                            name="companyForm"
                            validateMessages={validateMessages}
                        >
                            <CompanyForm mode={mode} companyForm={companyForm} businessTypes={businessTypes} />
                        </Form>
                    </div>
                </HrnModal>
            </div>
            <div>
                <Table
                    rowKey="id"
                    columns={tableUtils.buildColumns(
                        getCompaniesResult.isSuccess ? dataSource : [],
                        CompaniesColumn,
                        tableUtils.editDeleteActionColumns(handleEdit, handleDelete),
                        [],
                        undefined,
                        { serialNo: true }
                    )}
                    dataSource={dataSource}
                    size="middle"
                    className="ant_table"
                    loading={{
                        spinning: (getCompaniesResult.isFetching || deleteCompanyResult.isLoading) as boolean,
                        indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
                    }}
                    pagination={tableUtils.pagination(20, () => true)}
                />
            </div>
        </>
    );
};

export default Company;
